class PredictiveSearch extends withAnimation(SearchForm) {
  constructor() {
    super();

    this.cachedResults = {};
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.isInitialOpen = true;
    this.scrollLockEnable = true;
    this.closeByKeyboard = true;

    this.initialized = false;

    this.scheduleIdleInit();
  }

  scheduleIdleInit() {
    const init = () => this.initComponent();

    if ('requestIdleCallback' in window) {
      requestIdleCallback(init, { timeout: 2000 });
    } else {
      setTimeout(init, 0);
    }
  }

  initComponent() {
    if (this.initialized) return;
    this.initialized = true;

    this.searchTemplate = this.closest('.template-search__search');
    this.predictiveSearchWrapper = this.querySelector('[data-predictive-search-wrapper]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.predictiveSearchToggle = this.querySelector('[data-predictive-search-toggle]');
    this.predictiveSearchClose = this.querySelectorAll('[data-predictive-search-close]');
    this.scrollTrack = this.querySelector('custom-scrollbar');
    this.backDrop = this.querySelector('.modal__backdrop');
    this.modalTop = this.querySelector('.predictive-search__modal-top');
    this.customCursor = this.querySelector('custom-cursor');

    DrawerManager.register(this);

    this.isFull = this.predictiveSearchToggle ? true : false;

    this.initAnimation({
      duration: 0.3,
      staggerDuration: this.isFull ? 0.35 : 0.2,
      stageDelay: this.isFull ? 0.1 : 0.05,
      openedPosition: this.isFull ? 'fixed' : 'absolute'
    });

    this.setupEventListeners();
  }

  get isDrawerMode() {
    return window.DeviceUtils.isMobile && !this.searchTemplate;
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.input.addEventListener('click', this.onInputClick.bind(this));
    this.input.addEventListener('focusout', this.onInputFocusOut.bind(this));

    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));

    this.isFull && this.predictiveSearchToggle.addEventListener('click', this.onToggleClick.bind(this));
    this.backDrop?.addEventListener('click', this.onClickClose.bind(this));
    this.predictiveSearchClose.forEach((button) => {
      button.addEventListener('click', this.onClickClose.bind(this));
    });
  }

  getQuery() {
    return this.input.value.trim();
  }

  onToggleClick() {
    this.isOpen ? this.close(false, true) : this.onFocus();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (this.searchTemplate) {
      if (newSearchTerm.length == 0) {
        this.close(false, true);
        this.renderSearchResults('');
      } else {
        this.open();
      }
    }
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);
    this.searchTerm = newSearchTerm;
    if (!this.searchTerm.length) {
      this.removeAttribute('results');
      return;
    }
    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
      if (this.searchTemplate) {
        this.renderSearchResults('');
      }
    }
  }

  onInputClick() {
    this.isDrawerMode && document.body.classList.add('has-menu-disabled');
  }

  onInputFocusOut() {
    this.isDrawerMode && document.body.classList.remove('has-menu-disabled');
  }

  onFocus() {
    if (!this.initialized) {
      this.initComponent();
    }

    const currentSearchTerm = this.getQuery();
    this.updateMaxHeight();

    if (this.searchTemplate && currentSearchTerm.length && !this.isOpen) {
      if (this.searchTerm !== currentSearchTerm) {
        this.searchTerm = currentSearchTerm;
        this.getSearchResults(currentSearchTerm);
      } else if (this.getAttribute('results') !== 'true') {
        this.getSearchResults(this.searchTerm);
      } else {
        this.open();
      }
      return;
    }

    this.open();
    if (!currentSearchTerm.length) return;
    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute('results') !== 'true') {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut(event) {
    if (this.isDrawerMode || !this.closeByKeyboard) return;
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close(false, this.isFull);
    });
  }

  onKeyup(event) {
    event.preventDefault();
    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        if (this.isFull && !this.isOpen) {
          this.onFocus();
          return;
        }
        this.selectOption();
        break;
      case 'Escape':
        if (this.isFull && this.isOpen) {
          this.input.blur();
          setTimeout(() => {
            this.close(false, true);
            if (this.predictiveSearchToggle) {
              this.predictiveSearchToggle.focus();
            }
          }, 0);
          event.stopPropagation();
          return;
        }
        if (!this.isOpen) return;
        this.input.blur();
        setTimeout(() => {
          this.close(false, true);
        }, 0);
        event.stopPropagation();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  onClickClose() {
    this.close(false, true);
    this.closeByKeyboard = false;
    setTimeout(() => {
      this.closeByKeyboard = true;
    }, 500);
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      const matches = currentButtonText.match(new RegExp(previousTerm, 'gi')) || [];
      if (matches.length > 1) {
        return;
      }
      const newButtonText = currentButtonText.replace(new RegExp(previousTerm, 'i'), newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    const allVisibleElements = Array.from(this.querySelectorAll('li a, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );

    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i < allVisibleElements.length) {

      if (allVisibleElements[i] === selectedElement || allVisibleElements[i].parentElement === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    if (this.statusElement) this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];
    const parentElement = activeElement.parentElement;

    parentElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', parentElement.id || activeElement.id);

    activeElement.focus();
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();
    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      this.setLiveRegionResults();
      return;
    }
    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          return '';
        }

        return response.text();
      })
      .then(async (text) => {
        if (!text) {
          this.removeAttribute('loading');
          this.removeAttribute('results');
          return;
        }

        const parsedResultsSection = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search');

        if (!parsedResultsSection) {
          this.removeAttribute('loading');
          this.removeAttribute('results');
          return;
        }

        const resultsMarkup = parsedResultsSection.innerHTML;
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);

        await sleep(200)
        this.setLiveRegionResults();
      })
      .catch((error) => {
        if (error?.name === 'AbortError' || error?.code === 20) return;
        this.removeAttribute('loading');
        this.removeAttribute('results');
        console.warn('Predictive search request failed', error);
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  scrollTop() {
    const predictiveSearchScroll = this.querySelectorAll('.predictive-search-scroll-wrapper, .predictive-search-results__scroll');
    predictiveSearchScroll.forEach((scroll) => {
      scroll.scrollTop = 0;
    });
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);
    this.scrollTop();
    this.animateSearchResultsStagger();

    if (this.searchTemplate && !this.isOpen) {
      this.open();
    }
  }

  animateSearchResultsStagger() {
    const staggerSelector = '.predictive-search__result-group';

    const staggerItems = this.predictiveSearchResults.querySelectorAll(staggerSelector);
    if (!staggerItems.length) {
      return;
    }

    this.animateStagger(this.predictiveSearchResults, {
      staggerSelector: staggerSelector,
      animationType: 'fade',
      slideDirection: 'right',
      delay: 0
    });
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    if (this.isFull) return null;

    const bottomPosition = this.searchTemplate
      ? this.searchTemplate.getBoundingClientRect().bottom
      : (document.querySelector('.section-header')?.getBoundingClientRect().bottom || 0);

    const offset = this.searchTemplate ? 20 : 30;

    return Math.round(window.innerHeight - bottomPosition - offset);
  }

  updateMaxHeight() {
    if (this.isFull) return;
    const maxHeight = this.getResultsMaxHeight();
    document.documentElement.style.setProperty('--predictive-search-max-height', `${maxHeight}px`);
  }

  open() {
    if (this.isOpen) return;
    this.customCursor?.disable?.();;

    const megaMenu = document.querySelector('mega-menu');
    if (megaMenu?.classList.contains('mega-menu--block')) {
      megaMenu.classList.remove('mega-menu--block');
      document.body.removeAttribute('data-mega-menu-block');
      megaMenu.forceClose?.();
    }

    const isDrawerMode = this.isDrawerMode;

    if (isDrawerMode) {
      this.scrollLockEnable && scrollLock.lock();
      DeviceUtils.updateHeaderSizes();
      DrawerManager.setActiveWithSecondary(this);
      if (DrawerManager.diffrentSide()) {
        DrawerManager.clearSecondaryIfOpen(this);
      }
      document.body.classList.add('overflow-hidden');
    }
    this.updateMaxHeight();
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;

    if (this.isInitialOpen) {
      requestAnimationFrame(() => {
        if (this.isFull && !isDrawerMode) {
          document.body.classList.add('overflow-hidden');
          const modalTopHeight = this.modalTop?.getBoundingClientRect().height || 0;
          document.documentElement.style.setProperty('--predictive-search-top-max-height', `${modalTopHeight}px`);
        }
        this.animateShowContent(isDrawerMode);
      });
      this.isInitialOpen = false;
    }
  }

  close(clearSearchTerm = false, animateEnable = false) {
    if (this._isClosing) return;
    this._isClosing = true;

    this.isOpen = false;
    this.isInitialOpen = true;
    this.closeResults(clearSearchTerm, animateEnable);
  }

  closeResults(clearSearchTerm = false, animateEnable = false) {
    this.scrollTop();
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');
    if (selected) selected.setAttribute('aria-selected', false);
    this.removeAttribute('loading');

    const isDrawerMode = this.isDrawerMode;

    if (this.scrollLockEnable && animateEnable) {
      if (isDrawerMode || !this.input.matches(':focus')) {
        requestAnimationFrame(() => this.animateHideContent(isDrawerMode));
      }
    } else {
      this.onCloseActions(false)
    }
  }

  onCloseActions() {
    this._isClosing = false;
    if (this.isOpen) return;
    document.body.classList.remove('predictive-search-open');
    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
    this.customCursor?.disable?.();

    if (this.isDrawerMode) {
      DrawerManager.unsetActive(this);
      document.body.classList.remove('has-menu-disabled');
    } else {
      document.body.classList.remove('overflow-hidden');
    }
  }

  onShowActions(isDrawerMode) {
    document.body.classList.add('predictive-search-open');
    if (this.isFull && !isDrawerMode) this.input.focus();
    this.scrollTrack?.updateThumbPosition?.();
    if (isDrawerMode) {
      DrawerManager.clearSecondaryIfOpen(this);
    }
    this.customCursor?.enable?.();
  }

  forceClose() {
    if (!this.isOpen) return;
    this.close();
    this.input.blur();
    if (this.backDrop) this.backDrop.style.opacity = '0';
  }

  async animateShowContent(isDrawerMode) {
    const method = isDrawerMode ? 'animateSlideIn' : this.isFull ? 'animateSlideIn' : 'animateShow';
    const options = {
      slideDirection: isDrawerMode ? 'left' : 'top',
      animationType: 'fade',
      backDrop: isDrawerMode ? this.backDrop : this.isFull ? this.backDrop : false,
      onComplete: () => this.onShowActions(isDrawerMode),
      ...(!isDrawerMode && {
        staggerSelector: '.predictive-search__result-group',
        maxHeight: this.getResultsMaxHeight()
      })
    };

    await this[method](this.predictiveSearchWrapper, options);
  }

  async animateHideContent(isDrawerMode) {
    const method = isDrawerMode ? 'animateSlideOut' : this.isFull ? 'animateSlideOut' : 'animateHide';
    const options = {
      slideDirection: isDrawerMode ? 'right' : 'top',
      backDrop: isDrawerMode ? this.backDrop : this.isFull ? this.backDrop : false,
      onComplete: () => this.onCloseActions()
    };

    await this[method](this.predictiveSearchWrapper, options);
  }
}

customElements.define('predictive-search', PredictiveSearch);